import Template from '../components/template'
import { useCallback, useEffect, useState } from 'react'
import { workflows as workflowsApi } from '../lib/api'
import { Workflow } from '../lib/types'
import Loading from '../components/loading'
import WorkflowCard from './workflow-card/index'
import ErrorMsg from '../components/error-msg'
import {
    getCurrentPage,
    hasWorkflowAccess,
    setCurrentPage,
    workflowStatusOptions,
} from './util'
import Pagination from '../components/pagination'
import auth from '../lib/auth'
import { useNavigate } from 'react-router-dom'
import { Button, Search, Select, SelectItem } from '@carbon/react'
import { Add } from '@carbon/icons-react'
import { useDebouncedCallback } from 'use-debounce'
import NoDataFound from '../components/no-data-found/NoDataFound'

const PAGE_SIZE = 8

export default function WorkflowsPage() {
    const [search, setSearch] = useState(() => localStorage.getItem('workflow.search') || '')
    const [workflows, setWorkflows] = useState<Array<Workflow>>([])
    const [loading, setLoading] = useState(true)
    const [err, setErr] = useState('')
    const [page, setPage] = useState(0)
    const hasAdminRole = auth.checkRole('admin')
    const hasAdminAccess = auth.hasAdminAccess()
    const [filter, setFilter] = useState(() => localStorage.getItem('workflow.filter') || 'active')
    const navigate = useNavigate()

    const updateFilter = useCallback((value: string) => {
        setLoading(true)
        setFilter(value)
        localStorage.setItem('workflow.filter', value)
    }, [])

    const handleCreateWorkflow = () => {
        navigate('/workflows/_/edit')
    }

    const handleSearch = (value: string) => {
        setLoading(true)
        setSearch(value)
        setPage(0)
        localStorage.setItem('workflow.search', value)
    }

    useEffect(() => {
        const pagenum = getCurrentPage()
        setPage(pagenum)
    }, [])

    const getWorkflows = useDebouncedCallback((filter: string, search: string) => {
        workflowsApi
            .list(filter, search)
            .then((wf) => {
                let workflowsWithAccess = wf.filter((flow) => {
                    if (hasAdminAccess) {
                        return true;
                    }

                    // For non-admin users, only show active workflows
                    if (flow.statusId !== 1) {
                        return false;
                    }

                    return flow.users?.some((user) => hasWorkflowAccess(user)) || false;
                })
                setWorkflows(workflowsWithAccess)
                setLoading(false)
            })
            .catch((err) => setErr(err.message || err))
    }, 2000)

    useEffect(() => {
        getWorkflows(filter, search)
        // eslint-disable-next-line
    }, [filter, search])

    return (
        <Template auth>
            <div className='container p-6 mx-auto'>
                <div className='flex flex-row my-6 justify-between items-center'>
                    <h1>Workflows</h1>
                    {loading && <Loading />}
                    <div className='flex flex-row items-center'>
                        <Search
                            size='sm'
                            placeholder='Search workflows'
                            labelText='Search'
                            style={{ borderRadius: 0, boxShadow: 'initial', width: '100%' }}
                            closeButtonLabelText='Clear search input'
                            id='workflow-search'
                            value={search}
                            onChange={(e) => handleSearch(e.target.value)}
                        />

                        {hasAdminAccess && (
                            <div className='flex flex-row items-center mx-2'>
                                <Select
                                    id='workflow-filter'
                                    labelText=''
                                    className='bg-[#F4F4F4] w-28'
                                    hideLabel={true}
                                    inline={true}
                                    size='sm'
                                    value={filter}
                                    onChange={(e) => updateFilter(e.target.value)}
                                >
                                    {workflowStatusOptions.map(({ value, label }) => (
                                        <SelectItem key={value} value={value} text={label} />
                                    ))}
                                </Select>
                            </div>
                        )}
                        {hasAdminRole && (
                            <div>
                                <Button onClick={handleCreateWorkflow} renderIcon={Add} size='sm'>
                                    Create New Workflow
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
                {err && <ErrorMsg error={err} />}
                {workflows.length > 0 && (
                    <div
                        className={`grid grid-cols-2 grid-rows-${Math.ceil(
                            PAGE_SIZE / 2,
                        )} auto-rows-min gap-2 min-h-[70svh]`}
                    >
                        {workflows
                            .slice(PAGE_SIZE * page, PAGE_SIZE * (page + 1))
                            .map((workflow) => (
                                <WorkflowCard key={workflow.id} workflow={workflow} />
                            ))}
                    </div>
                )}
                {workflows.length === 0 && !loading && <NoDataFound />}
                <Pagination
                    page={page}
                    total={workflows.length}
                    onChange={(p) => setCurrentPage(setPage, p)}
                    pageSize={PAGE_SIZE}
                />
            </div>
        </Template>
    )
}
